
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle, AlertTriangle, Info } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  addToast: (message: string, type: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const toastConfig = {
      success: { icon: CheckCircle, color: 'border-brand-green' },
      error: { icon: AlertTriangle, color: 'border-red-500' },
      info: { icon: Info, color: 'border-blue-500' }
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3">
        {toasts.map((toast) => {
            const config = toastConfig[toast.type];
            const Icon = config.icon;
            return (
              <div
                key={toast.id}
                className={`flex items-center gap-4 w-full max-w-sm p-4 rounded-xl shadow-2xl bg-brand-light border-l-4 ${config.color} animate-fade-in-up border border-white/5`}
              >
                <Icon className={`w-6 h-6 ${config.color.replace('border-', 'text-')}`} />
                <p className="text-white font-semibold text-sm">{toast.message}</p>
              </div>
            );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
